import React from 'react';
import PropTypes from 'prop-types';
import ReactPlayer from 'react-player';
import { ENTERED } from 'react-transition-group/Transition';

import TouchBlocker from './shared/TouchBlocker';
import utils from '../../../common/utils';
import fabric from '../../../common/fabric';
import { Matrix } from '../../../common/lib/matrix';
import CloudinaryVideo from './CloudinaryVideo';
/**
 * Video component
 *
 * @param props
 * @returns {XML}
 * @constructor
 */

const Video = props => {
  const [params, setParams] = React.useState(props.params);

  const {
    style,
    matrixGroup,
    transitionStatus,
    windowHasFocus,
    globalScale,
    visible,
  } = props;
  const { shadow } = params;
  const {
    autoplay,
    endTime,
    startTime,
    url,
    mute,
    controls,
    loop,
  } = params.ludus;

  const groupMatrix = props.matrixGroup
    ? fabric.util.multiplyTransformMatrices(
        matrixGroup,
        params.ludus.transformMatrix,
      )
    : params.ludus.transformMatrix;

  const transformMatrix = fabric.util.multiplyTransformMatrices(
    [globalScale, 0, 0, globalScale, 0, 0],
    groupMatrix,
  );

  React.useEffect(() => {
    setParams(props.params);
  }, [props.params]);

  const renderThirdPartyVideo = () => {
    const volume = mute ? 0 : 0.8;
    const actualAutoplay = autoplay && !utils.isMobileDevice() && visible;
    const _endTime = !startTime || endTime > startTime ? endTime : null;
    const _mute = mute && visible;
    return (
      <div style={shadowStyles}>
        <TouchBlocker
          style={blockStyles}
          enabled={utils.isMobileDevice()}
          windowHasFocus={windowHasFocus}
        >
          <div style={innerStyles}>
            {[ENTERED].includes(transitionStatus) && (
              <ReactPlayer
                key={`${url}${controls}${mute}${loop}${startTime}${_endTime}`}
                url={url}
                width={blockStyles.width}
                height={blockStyles.height}
                playing={actualAutoplay}
                controls={controls}
                loop={loop}
                mute={_mute}
                volume={volume}
                playsinline
                config={{
                  vimeo: {
                    playerOptions: {
                      autoplay: +actualAutoplay,
                    },
                  },
                  youtube: {
                    playerVars: {
                      start: startTime,
                      end: _endTime,
                      autoplay: +actualAutoplay,
                      controls: +controls,
                      mute: +_mute,
                    },
                    embedOptions: {
                      rel: 0,
                      modestbranding: 1,
                    },
                  },
                }}
              />
            )}
          </div>
        </TouchBlocker>
      </div>
    );
  };

  // we decompose the transform matrix to apply scale to block dimension's
  // and keep a video player interface at 1:1
  const options = fabric.util.qrDecompose(transformMatrix);

  // filters
  const c = params.contrast ? params.contrast / 100 + 1 : 1;
  const b = params.brightness ? params.brightness / 100 + 1 : 1;
  const s = params.saturation ? params.saturation / 100 + 1 : 1;
  const invert = params.invert ? 1 : 0;
  const hasColorCorrection = b !== 1 || c !== 1 || s !== 1 || invert !== 0;
  const filter = hasColorCorrection
    ? `saturate(${s}) brightness(${b}) contrast(${c}) invert(${invert})`
    : 'none';

  // we have to render the iframes at the smallest size possible for better performance (and not crashing on low end device)
  // we apply the actual size thus have to revert the scale from the transform operations via the matrix lib

  const matrix = new Matrix(transformMatrix);

  const opts = matrix.decompose();

  const flipY = Math.sign(options.scaleY);
  const flipX = Math.sign(options.scaleX);

  const width = params.width * options.scaleX;
  const height = params.height * options.scaleY;
  const transform = `translate(-50%, -50%) translate(${options.translateX}px, ${options.translateY}px) rotate(${options.angle}deg)  scale(${flipX}, ${flipY})  skew(${opts.skew.x}rad, ${options.skewY}deg)`;

  const rx = params.rx * props.globalScale;
  const ry = params.ry * props.globalScale;

  const blockStyles = {
    ...style,
    borderRadius: `${rx}px/${ry}px`,
    overflow: 'hidden',
    pointerEvents: 'auto',
    position: 'absolute',
    left: 0,
    top: 0,
    width,
    height,
    opacity: props.groupOpacity,
    transform,
    filter: 'none',
  };
  const innerStyles = {
    overflow: 'hidden',
    filter,
  };

  // must work on skew so we need to apply it as filter
  const shadowStyles = {
    transform: shadow ? 'translateZ(0px)' : 'none',
    mixBlendMode:
      params.globalCompositeOperation === 'source-over'
        ? 'normal'
        : params.globalCompositeOperation,
    filter: shadow
      ? utils.getDropShadowCSSFilterFromFabricShadow(
          shadow,
          0,
          globalScale,
          false,
        )
      : 'none',
  };

  if (params.ludus.source === 'Cloudinary') {
    return (
      <CloudinaryVideo
        params={params}
        shadowStyles={shadowStyles}
        blockStyles={blockStyles}
        innerStyles={innerStyles}
        visible={visible || false}
      />
    );
  }
  if (params.ludus.url) {
    return renderThirdPartyVideo();
  }
  return null;
};

Video.propTypes = {
  style: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ).isRequired,
  params: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.bool,
      PropTypes.func,
      PropTypes.number,
      PropTypes.object,
      PropTypes.string,
    ]),
  ).isRequired,
  windowHasFocus: PropTypes.bool.isRequired,
  groupOpacity: PropTypes.number.isRequired,
  matrixGroup: PropTypes.arrayOf(PropTypes.number),
  transitionStatus: PropTypes.string.isRequired,
  globalScale: PropTypes.number,
  visible: PropTypes.bool,
};
Video.defaultProps = {
  hyperlink: null,
  matrixGroup: null,
};

export default Video;
